/* eslint-disable prefer-destructuring */

import { convertWpEmbedToWeb } from "@/utils/convertEmbed";

export function parseContent(html: string): string {
  let parsedHtml = cleanUnnecessarySpaces(html);
  const baseURL = process.env.WORDPRESS_BASE_ASSETS_URL;
  const cloudinaryBase = process.env.WORDPRESS_CLOUDINARY_ASSETS_BASE_URL;

  if (baseURL && cloudinaryBase) {
    const escapedBaseURL = baseURL.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&");

    const regex = new RegExp(
      `${escapedBaseURL}(\\d{4})\\/(\\d{2})\\/([^\\s]+?)(-\\d+x\\d+)?`,
      "g"
    );

    parsedHtml = parsedHtml.replace(regex, `${cloudinaryBase}$3`);
  }

  parsedHtml = decodeHtmlEntities(parsedHtml);

  // Remove dimensions from Cloudinary URLs
  // const cloudinaryRegex =
  //   /https:\/\/res\.cloudinary\.com\/[^/]+\/image\/upload\/[^/]+\/([^-\s]+)(-\d+x\d+)?/g;
  // parsedHtml = parsedHtml.replace(
  //   cloudinaryRegex,
  //   (_match, p1, _p2) =>
  //     `https://res.cloudinary.com/dv6hrcluu/image/upload/wordpress_assets/${p1}`
  // );
  // // should replace iem1dxsxtbg0f4zo3g0t-1-1024x512.jpg  with just iem1dxsxtbg0f4zo3g0t
  // const cloudinaryRegexRemoveVersion =
  //   /https:\/\/res\.cloudinary\.com\/dv6hrcluu\/image\/upload\/wordpress_assets\/([a-zA-Z0-9_-]+)-[0-9]+-[0-9]+x[0-9]+(\.[a-zA-Z0-9]+)?/g;

  // parsedHtml = parsedHtml.replace(
  //   cloudinaryRegexRemoveVersion,
  //   (_match, p1) =>
  //     `https://res.cloudinary.com/dv6hrcluu/image/upload/wordpress_assets/${p1}`
  // );

  parsedHtml = parseVideos(parsedHtml);

  return parsedHtml;
}

function enforceYouTubeRel0(html: string): string {
  // Capture the full host (with or without www.)
  return html.replace(
    /(<iframe[^>]+?(?:\s(?:src|data-src)=["']))(?:https?:)?\/\/((?:www\.)?youtube(?:-nocookie)?\.com)\/embed\/([a-zA-Z0-9_-]+)([^"']*)(["'][^>]*>)/gi,
    (_m, pre, host, videoId, tail, post) => {
      // normalize/prepare query string
      let qs = tail || "";
      if (!qs) {
        qs = "?";
      } else if (!qs.startsWith("?")) {
        qs = `?${qs.replace(/^&/, "")}`;
      }

      // upsert helper
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      const upsert = (q: string, key: string, val: string) =>
        q.includes(`${key}=`)
          ? q.replace(new RegExp(`${key}=[^&]*`, "i"), `${key}=${val}`)
          : `${q}${q.endsWith("?") ? "" : "&"}${key}=${val}`;

      qs = upsert(qs, "rel", "0");

      // ensure www. for YouTube hosts (nocookie especially)
      let normalizedHost = host;
      if (!/^www\./i.test(normalizedHost)) {
        normalizedHost = /youtube-nocookie\.com$/i.test(normalizedHost)
          ? "www.youtube-nocookie.com"
          : "www.youtube.com";
      }

      // use explicit https to avoid protocol-relative quirks
      return `${pre}https://${normalizedHost}/embed/${videoId}${qs}${post}`;
    }
  );
}

function parseVideos(html: string): string {
  let parsedHtml = convertWpEmbedToWeb(html);
  parsedHtml = parsedHtml.replace(/(\?|&)t=\d+s/g, "");

  // Regular expressions for different platforms
  const videoPatterns = [
    {
      regex:
        /https:\/\/(?:www\.)?youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/)([a-zA-Z0-9_-]+)(?:[/?#][^\s<]*)?/g,
      embed: (videoId: string) => embedYouTube(videoId),
    },
    {
      regex: /https:\/\/youtu\.be\/([a-zA-Z0-9_-]+)(?:[/?#][^\s<]*)?/g,
      embed: (videoId: string) => embedYouTube(videoId),
    },
    {
      regex: /https:\/\/player\.vimeo\.com\/video\/(\d+)/g,
      embed: (videoId: string) => embedVimeo(videoId),
    },
    {
      regex: /https:\/\/vimeo\.com\/(\d+)/g,
      embed: (videoId: string) => embedVimeo(videoId),
    },
    {
      regex: /https:\/\/www\.tiktok\.com\/@(.*?)\/video\/(\d+)/g,
      embed: (username: string, videoId: string) =>
        embedTikTok(username, videoId),
    },
    {
      regex: /https:\/\/twitter\.com\/([a-zA-Z0-9_]+)\/status\/(\d+)/g,
      embed: (username: string, tweetId: string) =>
        embedTwitter(username, tweetId),
    },
  ];

  const figurePatterns = [
    {
      regex:
        /<figure class="media">\s*https:\/\/(?:(?:www\.)?youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]+)(?:[/?#][^\s<]*)?\s*<\/figure>/g,
      embed: (videoId: string) => embedYouTube(videoId),
    },
    {
      regex:
        /<figure class="media">\s*https:\/\/vimeo\.com\/(\d+)\s*<\/figure>/g,
      embed: (videoId: string) => embedVimeo(videoId),
    },
    {
      regex:
        /<figure class="media">\s*https:\/\/player\.vimeo\.com\/video\/(\d+)\s*<\/figure>/g,
      embed: (videoId: string) => embedVimeo(videoId),
    },
  ];

  // Replace <oembed> tags with the captured URL
  const oembedRegex = /<oembed[^>]+url="([^"]+)"[^>]*><\/oembed>/gi;
  parsedHtml = parsedHtml.replace(oembedRegex, (_, url) => url);

  // Replace video URLs with iframes
  videoPatterns.forEach(({ regex, embed }) => {
    parsedHtml = parsedHtml.replace(regex, (match, ...groups: string[]) => {
      const isInsideIframe = new RegExp(
        `<iframe[^>]+src=["']${escapeRegExp(match)}[^"']*["']`,
        "i"
      ).test(parsedHtml);

      const isInsideAnchor = new RegExp(
        `<a[^>]*href=["']${escapeRegExp(match)}[^"']*["'][^>]*>`,
        "i"
      ).test(parsedHtml);

      // Skip replacement if the URL is inside an anchor or iframe tag
      if (!isInsideIframe && !isInsideAnchor) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        return embed(...groups); // Correctly pass groups as individual arguments
      }
      return match; // Return the original match if it's inside an iframe or anchor
    });
  });

  // Replace <figure> tags with iframes
  figurePatterns.forEach(({ regex, embed }) => {
    parsedHtml = parsedHtml.replace(regex, (match, videoId: string) =>
      embed(videoId)
    );
  });

  // Remove unnecessary query parameters
  const queryPatterns = [/&t=\d+s/, /\?share=copy/];
  queryPatterns.forEach((pattern) => {
    parsedHtml = parsedHtml.replace(pattern, "");
  });

  parsedHtml = enforceYouTubeRel0(parsedHtml);

  return parsedHtml;
}

// Helper function to escape special characters for use in RegExp
function escapeRegExp(str: string): string {
  return str.replace(/[.*+?^=!:${}()|\\]/g, "\\$&");
}

function embedYouTube(videoId: string): string {
  return `<iframe width="560" height="315" src="https://www.youtube-nocookie.com/embed/${videoId}?rel=0" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>`;
}

function embedVimeo(videoId: string): string {
  return `<iframe src="https://player.vimeo.com/video/${videoId}" width="640" height="360" frameborder="0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe>`;
}

function embedTikTok(username: string, videoId: string): string {
  return `<iframe src="https://www.tiktok.com/embed/v2/${videoId}" width="325" height="750" frameborder="0" allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture" allowfullscreen></iframe>`;
}

function embedTwitter(username: string, tweetId: string): string {
  return `<blockquote class="twitter-tweet"><a href="https://twitter.com/${username}/status/${tweetId}"></a></blockquote>`;
}

const cleanUnnecessarySpaces = (htmlContent: string): string => {
  if (typeof DOMParser === "undefined") {
    return htmlContent;
  }
  // Nothing to clean without <p> wrappers. Skip the DOMParser round-trip, which
  // would otherwise re-serialize the string (whitespace/entity normalization)
  // and diverge from the server render (where DOMParser is unavailable and the
  // input is returned as-is) — causing hydration mismatches on plain strings
  // like titles/alts that are parseContent()'d on both sides.
  if (!htmlContent || !htmlContent.includes("<p")) {
    return htmlContent;
  }
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, "text/html");

  // Get all p elements in the document
  const pElements = doc.querySelectorAll("p");

  pElements.forEach((p) => {
    // Check if the p element has only one child and that child is the specific span
    if (p.children.length === 1) {
      const child = p.children[0];
      if (
        (child?.tagName === "SPAN" &&
          /^s\d+$/.test(child?.className) && // Check for class name starting with 's' followed by digits
          child?.innerHTML.trim() === "<br>") ||
        (child?.tagName === "BR" &&
          child?.parentElement?.innerHTML === "<br>") ||
        (child?.tagName === "SPAN" && child?.innerHTML.trim() === "<br>")
      ) {
        // Remove the p element
        p.parentElement?.removeChild(p);
      }
    }
    // Check if the p element has two children: a span and a br
    if (p.children.length === 2) {
      const firstChild = p.children[0];
      const secondChild = p.children[1];

      if (
        firstChild?.tagName === "SPAN" &&
        /^s\d+$/.test(firstChild?.className) && // Check for class name starting with 's' followed by digits
        secondChild?.tagName === "BR" &&
        firstChild?.innerHTML.trim() === "" // Ensure the span is empty
      ) {
        // Remove the p element
        p.parentElement?.removeChild(p);
      }
    }
  });

  return doc.body.innerHTML;
};

const decodeHtmlEntities = (html: string): string =>
  html.replace(/&([a-zA-Z]+|#[0-9]+|#x[0-9a-fA-F]+);/g, (match, entity) => {
    if (entity.startsWith("#x")) {
      // Hexadecimal numeric entity
      return String.fromCharCode(parseInt(entity.substring(2), 16));
    }
    if (entity.startsWith("#")) {
      // Decimal numeric entity
      return String.fromCharCode(parseInt(entity.substring(1), 10));
    }
    // Named entity
    const entities: Record<string, string> = {
      amp: "&",
      lt: "<",
      gt: ">",
      apos: "'",
      nbsp: " ",
      copy: "©",
      reg: "®",
      euro: "€",
      cent: "¢",
      pound: "£",
      yen: "¥",
      sect: "§",
      deg: "°",
      plusmn: "±",
      sup2: "²",
      sup3: "³",
      micro: "µ",
      para: "¶",
      middot: "·",
      frac14: "¼",
      frac12: "½",
      frac34: "¾",
      iquest: "¿",
      times: "×",
      divide: "÷",
    };
    return entities[entity] ?? match;
  });
